import React from 'react';
import { motion } from 'framer-motion';
import { 
  Share2, 
  Target, 
  Mail, 
  QrCode, 
  FileText,
  ArrowDownRight
} from 'lucide-react';

const trafficSources = [
  {
    icon: Share2,
    title: 'Social Media Marketing',
    description: 'Strategic content distribution across platforms where your audience lives.',
    metrics: ['Instagram', 'LinkedIn', 'TikTok', 'YouTube']
  },
  {
    icon: Target,
    title: 'Paid Advertising',
    description: 'Targeted campaigns that put your offer in front of ready buyers.',
    metrics: ['Google Ads', 'Meta Ads', 'LinkedIn Ads']
  },
  {
    icon: Mail,
    title: 'Direct Mail Campaigns',
    description: 'Physical touchpoints that stand out in a digital world.',
    metrics: ['Postcards', 'Letters', 'Catalogs']
  },
  {
    icon: QrCode,
    title: 'QR Code Campaigns',
    description: 'Bridge offline and online with scannable entry points.',
    metrics: ['Events', 'Print', 'Packaging']
  },
  {
    icon: FileText,
    title: 'Organic Content',
    description: 'SEO-optimized content that attracts and educates your ideal customers.',
    metrics: ['Blogs', 'Podcasts', 'Videos']
  },
];

export default function AwarenessSection() {
  return (
    <section 
      id="awareness"
      data-testid="awareness-section"
      className="section-padding relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#050808] to-[#030303]" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4 block">
              Stage 1 / Awareness
            </span>
            <h2 
              data-testid="awareness-headline"
              className="text-4xl md:text-5xl font-bold text-white"
            >
              Traffic
              <br />
              <span className="gradient-text-cyan">Generation</span>
            </h2>
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 max-w-md text-base"
          >
            Multiple entry points feed into your funnel, creating a consistent 
            stream of potential customers.
          </motion.p>
        </div>
        
        {/* Traffic Sources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {trafficSources.map((source, index) => (
            <motion.div
              key={index}
              data-testid={`awareness-card-${index}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="group relative"
            >
              <div className="glass rounded-2xl p-6 h-full card-hover border border-cyan-500/10">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
                    <source.icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <ArrowDownRight className="w-5 h-5 text-cyan-400/50 group-hover:text-cyan-400 transition-colors" />
                </div>
                
                {/* Content */}
                <h3 className="text-lg font-semibold text-white mb-2">
                  {source.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  {source.description}
                </p>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {source.metrics.map((metric, i) => (
                    <span 
                      key={i}
                      className="text-xs px-2 py-1 rounded-full bg-white/5 text-gray-400"
                    >
                      {metric}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Glow effect on hover */}
              <div className="absolute inset-0 rounded-2xl bg-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity -z-10 blur-xl" />
            </motion.div>
          ))}
        </div>
        
        {/* Flow Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="flex justify-center mt-16"
        >
          <div className="flex flex-col items-center gap-4">
            <div className="flex gap-2">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{ 
                    y: [0, -10, 0],
                    opacity: [0.3, 1, 0.3]
                  }}
                  transition={{ 
                    duration: 2,
                    delay: i * 0.2,
                    repeat: Infinity
                  }}
                  className="w-2 h-2 rounded-full bg-cyan-400"
                />
              ))}
            </div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">
              Particles flowing into funnel
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
